import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { scrollState, clamp } from '@/lib/depth';

/**
 * About — composed as a fixed overlay around the clean hero2 (the cliff world),
 * exactly like the home text composes around hero1. The text *layers in* with
 * the identical motion language as the hero (slide-up + fade, same easing/delays)
 * the moment the viewer rests on hero2 — not a scroll-linked fade. It then
 * dissolves only on exit, as hero2 hands off to hero3.
 */
const PARAS = [
  "I started as a computer engineer, became a licensed attorney, and built an audience of more than 6 million people creating at the intersection of technology, AI, and culture. Along the way, I’ve partnered with Meta, Samsung, Microsoft, Nvidia, and most major Fortune 500 tech brands.",
  "I’m also building The King Below, a dark fantasy saga ten years in the making.",
  "This is where all of it lives.",
];

const IN_START = 1350; // viewer has rested on clean hero2 — layer the text in
const OUT_START = 3100; // begin dissolving as hero2 hands off to hero3
const OUT_END = 3700;

const EASE = [0.22, 1, 0.36, 1];

export const AboutOverlay = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    let raf;
    const tick = () => {
      const cam = scrollState.camera;
      // container only handles the EXIT fade; the entrance is the motion below
      const exit = clamp(1 - (cam - OUT_START) / (OUT_END - OUT_START), 0, 1);
      const shown = cam > IN_START;
      const op = shown ? exit : 0;
      if (ref.current) {
        ref.current.style.opacity = op.toFixed(3);
        ref.current.style.visibility = op < 0.002 ? 'hidden' : 'visible';
      }
      if (shown && !active) setActive(true); // latch the entrance once, like the home text
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  return (
    <div
      ref={ref}
      className="fixed inset-0 z-[8] flex items-center justify-end pointer-events-none"
      style={{ opacity: 0, visibility: 'hidden', transition: 'opacity 0.45s ease' }}
      aria-hidden="true"
      data-testid="about-overlay"
    >
      <motion.div
        className="max-w-[min(560px,86vw)] px-6 sm:pr-[7vw] text-left"
        style={{ textShadow: '0 2px 36px rgba(0,0,0,0.92)', transformPerspective: 1200, willChange: 'transform' }}
        initial={{ z: -130 }}
        animate={active ? { z: 0 } : { z: -130 }}
        transition={{ duration: 1.2, ease: EASE, delay: 0.05 }}
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.05 }}
          className="mb-3 font-mono-label text-[10px] uppercase tracking-[0.32em] text-primary/80"
        >
          About
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 28 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.15 }}
          className="font-display text-4xl sm:text-6xl text-cream leading-[1.04]"
        >
          I’m Joe Urbinati.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={active ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 1.2, delay: 0.6 }}
          className="mt-2 font-serif italic text-xl sm:text-2xl text-cream/90"
        >
          A storyteller, lawyer, and engineer.
        </motion.p>
        <div className="mt-6 space-y-4">
          {PARAS.map((p, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              transition={{ duration: 1, delay: 0.85 + i * 0.22, ease: EASE }}
              className="max-w-[40rem] text-sm sm:text-lg leading-relaxed text-cream/85"
            >
              {p}
            </motion.p>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default AboutOverlay;
